import axios from 'axios';
import { 
  DashboardData, 
  DailyKPI, 
  PricingConfig, 
  ThresholdsConfig, 
  NotificationsConfig,
  MqttConfig,
  DatabaseConfig,
  WebSocketConfig,
  TableMapping,
  DatabaseSchema,
  TargetField,
  Alert,
  AlertStats,
  SubstrateDelivery,
  CreateSubstrateDeliveryDto,
  MonthlyReconciliationReport,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const kpiService = {
  getDashboardData: async (): Promise<DashboardData> => {
    const response = await api.get<DashboardData>('/kpis/dashboard');
    return response.data;
  },

  getLatestKPI: async (): Promise<DailyKPI> => {
    const response = await api.get<DailyKPI>('/kpis/latest');
    return response.data;
  },

  getToday: async (): Promise<DailyKPI> => {
    const response = await api.get<DailyKPI>('/kpis/today');
    return response.data;
  },

  getKPIsByRange: async (days: number): Promise<DailyKPI[]> => {
    const response = await api.get<DailyKPI[]>(`/kpis/range/${days}`);
    return response.data;
  },

  getKPIsByDateRange: async (startDate?: string, endDate?: string): Promise<DailyKPI[]> => {
    const response = await api.get<DailyKPI[]>('/kpis', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  calculateKPI: async (date: string): Promise<DailyKPI> => {
    const response = await api.post<DailyKPI>(`/kpis/calculate/${date}`);
    return response.data;
  },
};

export const measurementService = {
  createFeedstock: async (data: any) => {
    const response = await api.post('/measurements/feedstock', data);
    return response.data;
  },

  createPower: async (data: any) => {
    const response = await api.post('/measurements/power', data);
    return response.data;
  },

  createGas: async (data: any) => {
    const response = await api.post('/measurements/gas', data);
    return response.data;
  },

  createSensor: async (data: any) => {
    const response = await api.post('/measurements/sensor', data);
    return response.data;
  },

  getRecent: async (hours: number = 24) => {
    const response = await api.get('/measurements/recent', {
      params: { hours },
    });
    return response.data;
  },

  getSentryCCR: async () => {
    const response = await api.get('/measurements/sentry-ccr');
    return response.data;
  },

  getSentryCCRHistory: async (range: 'week' | 'month' | 'year' | 'custom' = 'week', startDate?: string, endDate?: string) => {
    const response = await api.get('/measurements/sentry-ccr/history', {
      params: { range, startDate, endDate },
    });
    return response.data;
  },
};

export const configService = {
  getAll: async () => {
    const response = await api.get('/config');
    return response.data;
  },

  getByKey: async (key: string) => {
    const response = await api.get(`/config/${key}`);
    return response.data;
  },

  getPricing: async (): Promise<PricingConfig> => {
    const response = await api.get<PricingConfig>('/config/pricing/current');
    return response.data;
  },

  updatePricing: async (data: PricingConfig): Promise<PricingConfig> => {
    const response = await api.put<PricingConfig>('/config/pricing', data);
    return response.data;
  },

  getThresholds: async (): Promise<ThresholdsConfig> => {
    const response = await api.get<ThresholdsConfig>('/config/thresholds/current');
    return response.data;
  },

  updateThresholds: async (data: ThresholdsConfig): Promise<ThresholdsConfig> => {
    const response = await api.put<ThresholdsConfig>('/config/thresholds', data);
    return response.data;
  },

  getNotifications: async (): Promise<NotificationsConfig> => {
    const response = await api.get<NotificationsConfig>('/config/notifications/current');
    return response.data;
  },

  updateNotifications: async (data: NotificationsConfig): Promise<NotificationsConfig> => {
    const response = await api.put<NotificationsConfig>('/config/notifications', data);
    return response.data;
  },

  getMqttConfig: async (): Promise<MqttConfig> => {
    const response = await api.get<MqttConfig>('/config/mqtt/current');
    return response.data;
  },

  updateMqttConfig: async (data: MqttConfig): Promise<MqttConfig> => {
    const response = await api.put<MqttConfig>('/config/mqtt', data);
    return response.data;
  },

  getDatabaseConfig: async (): Promise<DatabaseConfig> => {
    const response = await api.get<DatabaseConfig>('/config/database/current');
    return response.data;
  },

  updateDatabaseConfig: async (data: DatabaseConfig): Promise<DatabaseConfig> => {
    const response = await api.put<DatabaseConfig>('/config/database', data);
    return response.data;
  },

  getWebSocketConfig: async (): Promise<WebSocketConfig> => {
    const response = await api.get<WebSocketConfig>('/config/websocket/current');
    return response.data;
  },

  updateWebSocketConfig: async (data: WebSocketConfig): Promise<WebSocketConfig> => {
    const response = await api.put<WebSocketConfig>('/config/websocket', data);
    return response.data;
  },

  testMqttConnection: async (data: MqttConfig): Promise<{ success: boolean; message: string; details?: any }> => {
    const response = await api.post('/config/mqtt/test', data);
    return response.data;
  },

  testDatabaseConnection: async (data: DatabaseConfig): Promise<{ success: boolean; message: string; details?: any }> => {
    const response = await api.post('/config/database/test', data);
    return response.data;
  },

  testWebSocketConfig: async (data: WebSocketConfig): Promise<{ success: boolean; message: string; details?: any }> => {
    const response = await api.post('/config/websocket/test', data);
    return response.data;
  },

  discoverDatabaseSchema: async (data: DatabaseConfig): Promise<{ success: boolean; message: string; schema?: DatabaseSchema }> => {
    const response = await api.post('/config/database/discover-schema', data);
    return response.data;
  },

  getTableMappings: async (): Promise<TableMapping[]> => {
    const response = await api.get<TableMapping[]>('/config/mappings/tables');
    return response.data;
  },

  updateTableMappings: async (mappings: TableMapping[]): Promise<any> => {
    const response = await api.put('/config/mappings/tables', mappings);
    return response.data;
  },

  getTargetEntities: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/config/mappings/target-entities');
    return response.data;
  },

  getTargetFields: async (entity: string): Promise<TargetField[]> => {
    const response = await api.get<TargetField[]>(`/config/mappings/target-fields/${entity}`);
    return response.data;
  },

  discoverTags: async (databaseConfig: DatabaseConfig, tableName: string, columnName: string, unitColumn?: string): Promise<any> => {
    const response = await api.post('/config/tags/discover', { 
      databaseConfig,
      tableName, 
      tagColumnName: columnName, 
      unitColumnName: unitColumn 
    });
    return response.data;
  },

  autoMapTags: async (tags: { name: string; unit?: string }[]): Promise<any> => {
    const response = await api.post('/config/tags/auto-map', tags);
    return response.data;
  },

  getTagMappings: async (): Promise<any[]> => {
    const response = await api.get('/config/tags/mappings');
    return response.data;
  },

  updateTagMappings: async (mappings: any[]): Promise<any> => {
    const response = await api.put('/config/tags/mappings', mappings);
    return response.data;
  },

  manualSync: async (): Promise<any> => {
    const response = await api.post('/config/sync/manual');
    return response.data;
  },

  getSyncStatus: async (): Promise<{ isSyncing: boolean }> => {
    const response = await api.get('/config/sync/status');
    return response.data;
  },
};

export const alertService = {
  getAll: async (params?: { type?: string; severity?: string; limit?: number }): Promise<Alert[]> => {
    const response = await api.get<Alert[]>('/alerts', { params });
    return response.data;
  },

  getRecent: async (limit: number = 10): Promise<Alert[]> => {
    const response = await api.get<Alert[]>('/alerts/recent', {
      params: { limit },
    });
    return response.data;
  },

  getStats: async (): Promise<AlertStats> => {
    const response = await api.get<AlertStats>('/alerts/stats');
    return response.data;
  },

  acknowledge: async (id: string): Promise<Alert> => {
    const response = await api.post<Alert>(`/alerts/${id}/acknowledge`);
    return response.data;
  },
};

export const analyticsService = {
  getMovingAverages: async (days: number = 30) => {
    const response = await api.get(`/analytics/moving-averages`, { params: { days } });
    return response.data;
  },

  getTrends: async (days: number = 30) => {
    const response = await api.get(`/analytics/trends`, { params: { days } });
    return response.data;
  },

  getCorrelations: async () => {
    const response = await api.get(`/analytics/correlations`);
    return response.data;
  },

  getForecast: async (metric: string = 'kpi_gas_yield_m3_per_kg', days: number = 7) => {
    const response = await api.get(`/analytics/forecast`, { params: { metric, days } });
    return response.data;
  },

  getAnomalies: async (metric: string = 'kpi_gas_yield_m3_per_kg', days: number = 30, threshold: number = 2.5) => {
    const response = await api.get(`/analytics/anomalies`, { params: { metric, days, threshold } });
    return response.data;
  },

  getSummary: async (days: number = 30) => {
    const response = await api.get(`/analytics/summary`, { params: { days } });
    return response.data;
  },

  getHourlyPower: async () => {
    const response = await api.get(`/analytics/hourly-power`);
    return response.data;
  },

  getPowerConsumption: async (
    range: 'week' | 'month' | 'year' | 'custom' = 'week', 
    startDate?: string, 
    endDate?: string,
    fillGaps: boolean = false
  ) => {
    const response = await api.get(`/analytics/power-consumption`, { 
      params: { range, startDate, endDate, fillGaps } 
    });
    return response.data;
  },

  getGasProduction: async (
    range: 'week' | 'month' | 'year' | 'custom' = 'week', 
    startDate?: string, 
    endDate?: string,
    fillGaps: boolean = false
  ) => {
    const response = await api.get(`/analytics/gas-production`, { 
      params: { range, startDate, endDate, fillGaps } 
    });
    return response.data;
  },
};

export const monitoringService = {
  getHealth: async () => {
    const response = await api.get('/monitoring/health');
    return response.data;
  },

  getMetrics: async () => {
    const response = await api.get('/monitoring/metrics');
    return response.data;
  },
};

export const substratesService = {
  /** Create a single delivery */
  createDelivery: async (dto: CreateSubstrateDeliveryDto): Promise<SubstrateDelivery> => {
    const response = await api.post<SubstrateDelivery>('/substrates/deliveries', dto);
    return response.data;
  },

  /** Bulk create deliveries */
  bulkCreateDeliveries: async (dtos: CreateSubstrateDeliveryDto[]): Promise<SubstrateDelivery[]> => {
    const response = await api.post<SubstrateDelivery[]>('/substrates/deliveries/bulk', dtos);
    return response.data;
  },

  /** List deliveries with optional filters */
  getDeliveries: async (params?: {
    startDate?: string;
    endDate?: string;
    commodityCode?: string;
    source?: string;
  }): Promise<SubstrateDelivery[]> => {
    const response = await api.get<SubstrateDelivery[]>('/substrates/deliveries', { params });
    return response.data;
  },

  /** Get a single delivery by id */
  getDelivery: async (id: string): Promise<SubstrateDelivery> => {
    const response = await api.get<SubstrateDelivery>(`/substrates/deliveries/${id}`);
    return response.data;
  },

  /** Update a delivery */
  updateDelivery: async (id: string, dto: Partial<CreateSubstrateDeliveryDto>): Promise<SubstrateDelivery> => {
    const response = await api.put<SubstrateDelivery>(`/substrates/deliveries/${id}`, dto);
    return response.data;
  },

  /** Delete a delivery */
  deleteDelivery: async (id: string): Promise<void> => {
    await api.delete(`/substrates/deliveries/${id}`);
  },

  /** Get the monthly reconciliation report */
  getMonthlyReport: async (year: number, month: number): Promise<MonthlyReconciliationReport> => {
    const response = await api.get<MonthlyReconciliationReport>('/substrates/report/monthly', {
      params: { year, month },
    });
    return response.data;
  },
};

export const labReportService = {
  getMonthlyReport: async (year: number, month: number) => {
    const response = await api.get('/lab-report/monthly', { params: { year, month } });
    return response.data;
  },

  getEntries: async (year: number, month: number) => {
    const response = await api.get('/lab-report/entries', { params: { year, month } });
    return response.data;
  },

  getAvailableYears: async (): Promise<number[]> => {
    const response = await api.get<number[]>('/lab-report/years');
    return response.data;
  },

  upsert: async (dto: any) => {
    const response = await api.post('/lab-report/upsert', dto);
    return response.data;
  },

  update: async (id: string, dto: any) => {
    const response = await api.put(`/lab-report/${id}`, dto);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/lab-report/${id}`);
  },
};

export const tssService = {
  /** Get structured daily report for a date (includes calculated avg, remark, %TSS) */
  getDailyReport: async (date: string) => {
    const response = await api.get('/lab-report/tss/report', { params: { date } });
    return response.data;
  },

  /** List all dates that have TSS entries */
  getAvailableDates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/tss/dates');
    return response.data;
  },

  /** Batch upsert entries for a date */
  upsertBatch: async (entries: any[]) => {
    const response = await api.post('/lab-report/tss/batch', { entries });
    return response.data;
  },

  /** Delete all TSS entries for a date */
  deleteByDate: async (date: string): Promise<void> => {
    await api.delete('/lab-report/tss/by-date', { params: { date } });
  },
};

export const tsService = {
  /** Get structured daily report for a date (includes calculated avg, remark, %TS) */
  getDailyReport: async (date: string) => {
    const response = await api.get('/lab-report/ts/report', { params: { date } });
    return response.data;
  },

  /** List all dates that have TS entries */
  getAvailableDates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/ts/dates');
    return response.data;
  },

  /** Batch upsert entries for a date */
  upsertBatch: async (entries: any[]) => {
    const response = await api.post('/lab-report/ts/batch', { entries });
    return response.data;
  },

  /** Delete all TS entries for a date */
  deleteByDate: async (date: string): Promise<void> => {
    await api.delete('/lab-report/ts/by-date', { params: { date } });
  },
};

export const vsService = {
  getDailyReport: async (date: string) => {
    const response = await api.get('/lab-report/vs/report', { params: { date } });
    return response.data;
  },
  getAvailableDates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/vs/dates');
    return response.data;
  },
  upsertBatch: async (entries: any[]) => {
    const response = await api.post('/lab-report/vs/batch', { entries });
    return response.data;
  },
  deleteByDate: async (date: string): Promise<void> => {
    await api.delete('/lab-report/vs/by-date', { params: { date } });
  },
};

export const substrateTsService = {
  getDailyReport: async (date: string) => {
    const response = await api.get('/lab-report/substrate-ts/report', { params: { date } });
    return response.data;
  },
  getAvailableDates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/substrate-ts/dates');
    return response.data;
  },
  getAvailableSubstrates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/substrate-ts/substrates');
    return response.data;
  },
  upsertBatch: async (entries: any[]) => {
    const response = await api.post('/lab-report/substrate-ts/batch', { entries });
    return response.data;
  },
  deleteByDate: async (date: string, substrateName?: string): Promise<void> => {
    await api.delete('/lab-report/substrate-ts/by-date', { params: { date, substrateName } });
  },
};

export const substrateVsService = {
  getDailyReport: async (date: string) => {
    const response = await api.get('/lab-report/substrate-vs/report', { params: { date } });
    return response.data;
  },
  getAvailableDates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/substrate-vs/dates');
    return response.data;
  },
  getAvailableSubstrates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/substrate-vs/substrates');
    return response.data;
  },
  upsertBatch: async (entries: any[]) => {
    const response = await api.post('/lab-report/substrate-vs/batch', { entries });
    return response.data;
  },
  deleteByDate: async (date: string, substrateName?: string): Promise<void> => {
    await api.delete('/lab-report/substrate-vs/by-date', { params: { date, substrateName } });
  },
};

export const nitrateNService = {
  getDailyReport: async (date: string) => {
    const response = await api.get('/lab-report/nitrate-n/report', { params: { date } });
    return response.data;
  },
  getAvailableDates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/nitrate-n/dates');
    return response.data;
  },
  upsertBatch: async (entries: any[]) => {
    const response = await api.post('/lab-report/nitrate-n/batch', { entries });
    return response.data;
  },
  deleteByDate: async (date: string): Promise<void> => {
    await api.delete('/lab-report/nitrate-n/by-date', { params: { date } });
  },
};

export const phosphateService = {
  getDailyReport: async (date: string) => {
    const response = await api.get('/lab-report/phosphate/report', { params: { date } });
    return response.data;
  },
  getAvailableDates: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/lab-report/phosphate/dates');
    return response.data;
  },
  upsertBatch: async (entries: any[]) => {
    const response = await api.post('/lab-report/phosphate/batch', { entries });
    return response.data;
  },
  deleteByDate: async (date: string): Promise<void> => {
    await api.delete('/lab-report/phosphate/by-date', { params: { date } });
  },
};

export default api;
